import { ComputerDesktopIcon, DevicePhoneMobileIcon, MapIcon } from "@heroicons/react/24/outline";
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import { Fragment, useCallback, useLayoutEffect, useRef, useState } from "react";
import { viewportLabel } from "@/components/audits/replay/helpers";
import type { LiveEvaluationInteraction, LiveEvaluationPage } from "@/types";

type Props = {
  pages: LiveEvaluationPage[];
  selectedPageId: string | null;
  activePageId: string | null;
  mappingInProgress?: boolean;
  onSelect: (pageId: string) => void;
  availableViewports: string[];
  activeViewport: string | null;
  onSelectViewport: (viewport: string) => void;
};

export default function LiveEvaluationPageTabs({
  pages,
  selectedPageId,
  activePageId,
  mappingInProgress = false,
  onSelect,
  availableViewports,
  activeViewport,
  onSelectViewport,
}: Props) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [fadeRight, setFadeRight] = useState(false);

  const updateFade = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) {
      setFadeRight(false);
      return;
    }

    setFadeRight(el.scrollWidth - el.clientWidth - el.scrollLeft > 1);
  }, []);

  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) {
      return;
    }

    updateFade();
    const observer = new ResizeObserver(updateFade);
    observer.observe(el);
    if (el.firstElementChild) {
      observer.observe(el.firstElementChild);
    }

    el.addEventListener("scroll", updateFade, { passive: true });
    window.addEventListener("resize", updateFade);

    return () => {
      observer.disconnect();
      el.removeEventListener("scroll", updateFade);
      window.removeEventListener("resize", updateFade);
    };
  }, [pages, updateFade]);

  if (pages.length === 0) {
    return null;
  }

  const selectedPage = pages.find((page) => page.id === selectedPageId) || null;
  const tabPages = orderedLiveEvaluationPages(pages, activePageId, mappingInProgress);

  return (
    <div className="flex min-w-0 border-b border-gray-100">
      <div className="relative min-w-0 flex-1">
        <div
          ref={scrollerRef}
          className="overflow-x-auto overflow-y-hidden overscroll-x-contain [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        >
          <div className="flex w-max flex-nowrap gap-1 px-5 pt-1 sm:px-6">
            {tabPages.map((page, index) => {
              const selected = page.id === selectedPageId;
              const live = page.id === activePageId;
              // Allow focusing a queued page (e.g. coverage Audit → Watch live) before browsing starts.
              const locked = !page.started && page.id !== activePageId && page.id !== selectedPageId;
              const queued = !live && !page.started;
              const kind = page.kind ?? "page";
              const label = kind === "page" ? page.path : page.title?.trim() || page.path;
              const previousKind = index > 0 ? (tabPages[index - 1].kind ?? "page") : null;
              const failedEmpty = pageHasFailedEmptyBrowse(page);
              const queuedTitle = queued ? "Enqueued to run" : undefined;
              const liveTitle = live ? "Live" : undefined;
              const tabTitle = liveTitle || queuedTitle;
              const queuedIcon = queued ? <QueuedHourglassIcon /> : null;
              const mapIcon =
                kind === "journey_map" ? <MapIcon className="h-4 w-4 shrink-0" aria-hidden="true" /> : null;
              const divider =
                previousKind !== null && previousKind !== kind ? (
                  <span className="mx-2 h-4 w-px shrink-0 self-center bg-gray-200" aria-hidden="true" />
                ) : null;

              if (locked) {
                return (
                  <Fragment key={page.id}>
                    {divider}
                    <span
                      title={tabTitle}
                      className="inline-flex shrink-0 cursor-not-allowed items-center gap-1.5 whitespace-nowrap border-b-2 border-transparent px-3 py-2.5 text-sm text-gray-300"
                    >
                      {queuedIcon}
                      {mapIcon}
                      {label}
                    </span>
                  </Fragment>
                );
              }

              return (
                <Fragment key={page.id}>
                  {divider}
                  <button
                    type="button"
                    title={tabTitle}
                    aria-label={tabTitle ? `${label}. ${tabTitle}` : undefined}
                    onClick={() => onSelect(page.id)}
                    className={`shrink-0 cursor-pointer whitespace-nowrap border-b-2 px-3 py-2.5 text-sm transition-colors ${
                      selected
                        ? "border-primary font-medium text-gray-900"
                        : "border-transparent text-gray-500 hover:border-gray-300/50 hover:text-gray-800"
                    }`}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {queuedIcon}
                      {mapIcon}
                      {label}
                      {live ? <LiveTabMark /> : null}
                      {!live && failedEmpty ? (
                        <ExclamationTriangleIcon
                          className="h-4 w-4 text-error"
                          title="Browse failed"
                          aria-label="Browse failed"
                        />
                      ) : null}
                    </span>
                  </button>
                </Fragment>
              );
            })}
          </div>
        </div>
        <div
          aria-hidden="true"
          className={`pointer-events-none absolute inset-y-0 right-0 w-10 bg-linear-to-l from-white to-transparent transition-opacity duration-200 ${
            fadeRight ? "opacity-100" : "opacity-0"
          }`}
        />
      </div>
      {availableViewports.length > 1 ? (
        <div className="flex shrink-0 items-center border-l border-gray-100 px-2 py-1.5 sm:px-3">
          <div className="inline-flex overflow-hidden rounded-md border border-gray-200">
            {availableViewports.map((viewport) => {
              const selected = viewport === activeViewport;
              const Icon = viewport === "mobile" ? DevicePhoneMobileIcon : ComputerDesktopIcon;
              const label = viewportLabel(viewport);
              const failedEmpty = isFailedEmptyBrowse(selectedPage?.interactions[viewport]);

              return (
                <button
                  key={viewport}
                  type="button"
                  aria-label={
                    failedEmpty
                      ? `Show ${label.toLowerCase()} recording (browse failed)`
                      : `Show ${label.toLowerCase()} recording`
                  }
                  aria-pressed={selected}
                  title={failedEmpty ? `${label} recording — browse failed` : `${label} recording`}
                  className={`flex cursor-pointer items-center gap-1.5 border-r border-gray-200 px-2 py-1 text-xs last:border-r-0 sm:px-2.5 ${
                    selected
                      ? failedEmpty
                        ? "bg-error-light/60 font-medium text-error-dark"
                        : "bg-primary/10 font-medium text-primary"
                      : failedEmpty
                        ? "bg-white text-error hover:bg-error-light/40"
                        : "bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-800"
                  }`}
                  onClick={() => onSelectViewport(viewport)}
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  <span className="hidden sm:inline">{label}</span>
                  {failedEmpty ? (
                    <ExclamationTriangleIcon className="h-3.5 w-3.5 text-error" aria-hidden="true" />
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Matches live player: failed empty pane only when status failed and no frames to replay. */
function isFailedEmptyBrowse(interaction: LiveEvaluationInteraction | undefined) {
  return Boolean(interaction && interaction.status === "failed" && interaction.screenshots.length === 0);
}

function LiveTabMark() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-1.5 py-0.5 text-[11px] font-semibold text-primary">
      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" aria-hidden="true" />
      Live
    </span>
  );
}

function orderedLiveEvaluationPages(
  pages: LiveEvaluationPage[],
  activePageId: string | null,
  mappingInProgress: boolean,
) {
  const pinWalking = (list: LiveEvaluationPage[]) => {
    if (!activePageId) {
      return list;
    }

    const walkingIndex = list.findIndex((page) => page.id === activePageId);
    if (walkingIndex <= 0) {
      return list;
    }

    const walking = list[walkingIndex];
    return [walking, ...list.filter((page) => page.id !== activePageId)];
  };

  if (mappingInProgress) {
    const mappingPages = pages.filter((page) => (page.kind ?? "page") === "journey_map");
    if (mappingPages.length > 0) {
      const rest = pages.filter((page) => (page.kind ?? "page") !== "journey_map");
      return [...mappingPages, ...pinWalking(rest)];
    }
  }

  return pinWalking(pages);
}

function QueuedHourglassIcon() {
  return (
    <svg
      className="h-4 w-4 shrink-0"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6.75 3.75h10.5M6.75 20.25h10.5M7.5 3.75v3.172a2.25 2.25 0 0 0 .659 1.591L12 12l3.841-3.487A2.25 2.25 0 0 0 16.5 6.922V3.75M7.5 20.25v-3.172a2.25 2.25 0 0 1 .659-1.591L12 12l3.841 3.487a2.25 2.25 0 0 1 .659 1.591v3.172"
      />
    </svg>
  );
}

function pageHasFailedEmptyBrowse(page: LiveEvaluationPage) {
  return Object.values(page.interactions).some((interaction) => isFailedEmptyBrowse(interaction));
}
